import { useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { Segment, Header, Table, Button, Dropdown, Label, Checkbox, Message } from 'semantic-ui-react';

/**
 * Who supports this organisation, and which of its streams they read.
 *
 * **Gated on `ORGANISATION: MANAGE`**, not on managing streams. Phase 9 separated exactly this:
 * a content reviewer curates what gets published, but whether another organisation reads it at
 * all is an organisation-level decision.
 *
 * Removing a supporter archives the relationship **and** the access it carried, together —
 * otherwise removing one would change nothing they could see.
 */

const GET_SUPPORTERS = gql`
  query SupporterOrganisations {
    canManageSupporters
    supporterOrganisations {
      supporterId organisationId name description streamIds memberCount
    }
    candidateSupporterOrganisations { id name memberCount }
    streams { id name isDefault }
  }
`;

const ADD_SUPPORTER = gql`
  mutation AddSupporterOrganisation($supporterOrganisationId: Int!, $streamIds: [Int!]!, $description: String) {
    addSupporterOrganisation(supporterOrganisationId: $supporterOrganisationId, streamIds: $streamIds, description: $description)
  }
`;

const REMOVE_SUPPORTER = gql`
  mutation RemoveSupporterOrganisation($supporterId: Int!) {
    removeSupporterOrganisation(supporterId: $supporterId)
  }
`;

const SET_SUBSCRIPTION = gql`
  mutation SetStreamSubscription($streamId: Int!, $subscriberOrganisationId: Int!, $subscribed: Boolean!) {
    setStreamSubscription(streamId: $streamId, subscriberOrganisationId: $subscriberOrganisationId, subscribed: $subscribed)
  }
`;

export const Supporters = () => {
  const { data, loading, refetch } = useQuery(GET_SUPPORTERS, { fetchPolicy: 'network-only' });
  const [addSupporter, { loading: adding }] = useMutation(ADD_SUPPORTER);
  const [removeSupporter] = useMutation(REMOVE_SUPPORTER);
  const [setSubscription] = useMutation(SET_SUBSCRIPTION);

  const [choosing, setChoosing] = useState(false);
  const [newOrg, setNewOrg] = useState<number | null>(null);
  const [newStreams, setNewStreams] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);

  if (loading || !data?.canManageSupporters) {
    return null;
  }

  const supporters = data.supporterOrganisations ?? [];
  const candidates = data.candidateSupporterOrganisations ?? [];
  const streams = data.streams ?? [];

  const act = async (fn: () => Promise<unknown>, failure: string) => {
    setError(null);
    try {
      await fn();
      await refetch();
    } catch (e: any) {
      setError(e?.message ?? failure);
    }
  };

  return (
    <Segment>
      <Header as="h3">Organisations that support us</Header>
      <p style={{ color: '#767676' }}>
        Which organisations can see our posts, and which streams each of them reads.
      </p>

      {error && <Message negative onDismiss={() => setError(null)}>{error}</Message>}

      {supporters.length === 0
        ? <p style={{ color: '#767676' }}>Nobody supports this organisation yet.</p>
        : (
          <Table compact>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Organisation</Table.HeaderCell>
                {streams.map((s: any) => (
                  <Table.HeaderCell key={s.id} textAlign="center">{s.name}</Table.HeaderCell>
                ))}
                <Table.HeaderCell />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {supporters.map((sup: any) => (
                <Table.Row key={sup.supporterId}>
                  <Table.Cell>
                    {sup.name}
                    <div style={{ color: '#767676', fontSize: '0.85em' }}>
                      {sup.memberCount} member{sup.memberCount === 1 ? '' : 's'}
                    </div>
                  </Table.Cell>
                  {/* The matrix: a tick is one organisation-subject grant, so it covers
                      everybody there including people who join later. */}
                  {streams.map((s: any) => (
                    <Table.Cell key={s.id} textAlign="center">
                      <Checkbox
                        checked={sup.streamIds.includes(s.id)}
                        onChange={(_, d) => act(
                          () => setSubscription({
                            variables: {
                              streamId: s.id,
                              subscriberOrganisationId: sup.organisationId,
                              subscribed: d.checked === true,
                            },
                          }),
                          'Could not change that.',
                        )}
                      />
                    </Table.Cell>
                  ))}
                  <Table.Cell collapsing textAlign="right">
                    <Button
                      size="tiny"
                      onClick={() => act(
                        () => removeSupporter({ variables: { supporterId: sup.supporterId } }),
                        'Could not remove that supporter.',
                      )}
                    >
                      Remove
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}

      {choosing
        ? (
          <Segment secondary>
            <Header as="h5">Add a supporting organisation</Header>
            <Dropdown
              search
              selection
              clearable
              placeholder="Which organisation?"
              style={{ marginRight: '0.5em', minWidth: '18em' }}
              value={newOrg ?? undefined}
              options={candidates.map((c: any) => ({
                key: c.id,
                text: `${c.name} (${c.memberCount} member${c.memberCount === 1 ? '' : 's'})`,
                value: c.id,
              }))}
              onChange={(_, d) => setNewOrg(d.value as number)}
            />
            <Dropdown
              multiple
              selection
              placeholder="Which streams may they read?"
              style={{ marginRight: '0.5em', minWidth: '18em' }}
              value={newStreams}
              options={streams.map((s: any) => ({
                key: s.id,
                text: s.isDefault ? `${s.name} (default)` : s.name,
                value: s.id,
              }))}
              onChange={(_, d) => setNewStreams(d.value as number[])}
            />
            <Button
              primary
              loading={adding}
              disabled={adding || newOrg == null}
              onClick={async () => {
                await act(
                  () => addSupporter({
                    variables: {
                      supporterOrganisationId: newOrg,
                      streamIds: newStreams,
                      description: null,
                    },
                  }),
                  'Could not add that supporter.',
                );
                setChoosing(false);
                setNewOrg(null);
                setNewStreams([]);
              }}
            >
              Add
            </Button>
            <Button onClick={() => { setChoosing(false); setNewOrg(null); setNewStreams([]); }}>
              Cancel
            </Button>
            {newStreams.length === 0 && newOrg != null && (
              <p style={{ color: '#767676', fontSize: '0.9em', marginTop: '0.5em' }}>
                With no streams chosen they will support us but see nothing. You can tick streams
                afterwards.
              </p>
            )}
          </Segment>
        )
        : candidates.length > 0 && (
          <Button size="small" onClick={() => setChoosing(true)}>Add a supporting organisation</Button>
        )}
    </Segment>
  );
};
